
import React, { useState, useEffect, useMemo } from 'react';
import { SmartSplitSuggestion, Order, SuggestedRoute, SystemConfig, DecisionReason, TimingGuidance, Driver, UserRole, DecisionTraceEntry, SafeDispatchAnalysis } from '../types';
import { Map as MapIcon, Cpu, Sparkles, Route, PlayCircle, Truck, ArrowRightLeft, Minimize2, Bike, Send, ShieldCheck, Timer, Snowflake, Flame, AlertCircle, Zap, Repeat2, X, ChevronRight, Users } from 'lucide-react';
import { DistanceService } from '../services/parserService';
import { toast } from 'react-hot-toast';
import { BaseTooltip } from './shared/BaseTooltip';
import { AuditDictionary } from '../services/logistics/core/auditCalculations';
import { ManualIntervention } from './strategy/ManualIntervention';
import { calculateSafeDispatchCountdown, analyzeSafeDispatch, formatETA, calculateSafeDispatchSequence } from '../services/logistics/core/timing';
import { COLD_START_MINS_PER_KM } from '../services/logistics/constants';
import { formatRouteTag, getFleetLabel } from '../services/logistics/core/routePresentation';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
    suggestion: SmartSplitSuggestion | null;
    pendingOrders?: Order[];
    onApplySuggestion: () => void;
    onDispatchAll: (orders: Order[], mode: 'own' | 'third') => void;
    onApplySequential?: () => void;
    onAcceptWait?: (orders: Order[]) => void;
    onIgnoreWait?: (orders: Order[]) => void;
    onManualAssignment?: (orderId: string, type: 'own' | 'third', reason?: string) => void;
    onBatchManualAssignment?: (
        ownIds: string[],
        thirdIds: string[],
        reason?: string,
        ownRouteGroups?: Record<string, string>,
        thirdRouteGroups?: Record<string, string>,
        ownActiveRouteByOrder?: Record<string, string>
    ) => void;
    activeDriverRoutes?: { driverId: string; driverName: string; activeOrders: number; isInProgress: boolean }[];
    isMapVisible: boolean;
    onToggleMap: () => void;
    onDispatchRoute: (orders: Order[], mode: 'own' | 'third', driverId?: string) => void;
    config: SystemConfig;
    secondsToNextUpdate?: number;
    openSofiaDecision?: (data: any) => void;
    handleConfirmStrategy?: (ids: string[], strategy: string) => void;
    handleConfirmBoomerangStrategy?: (goIds: string[], returnIds: string[]) => void;
    handleIgnoreStrategy?: (ids: string[], strategy: string, reason?: string) => void;
    isStrategyIgnored?: (orderId: string, strategy: string) => boolean; // v58.6
    drivers?: Driver[];
    userRole?: UserRole;
    isGlassMode?: boolean;
    onToggleGlassMode?: () => void;
}

export const CostComparator: React.FC<Props> = ({
    suggestion, pendingOrders = [], onApplySuggestion, onDispatchAll, onApplySequential, onAcceptWait, onIgnoreWait,
    onManualAssignment, onBatchManualAssignment, isMapVisible, onToggleMap, onDispatchRoute, config, secondsToNextUpdate = 0,
    openSofiaDecision, handleConfirmStrategy, handleConfirmBoomerangStrategy, handleIgnoreStrategy, isStrategyIgnored, drivers, userRole, isGlassMode, onToggleGlassMode,
    activeDriverRoutes = []
}) => {
    const [isAdjusting, setIsAdjusting] = useState(false);
    const [localFleet, setLocalFleet] = useState<Order[]>([]);
    const [localApps, setLocalApps] = useState<Order[]>([]);
    const [localTriage, setLocalTriage] = useState<Order[]>([]);
    const [showDecisionTrace, setShowDecisionTrace] = useState(false);

    useEffect(() => {
        if (suggestion && !isAdjusting) {
            setLocalFleet(suggestion.ownFleet);
            setLocalApps(suggestion.thirdParty);
            const routedIds = new Set([...suggestion.ownFleet, ...suggestion.thirdParty].map(o => o.id));
            const nonRouted = pendingOrders.filter(o => !routedIds.has(o.id));
            setLocalTriage(nonRouted);
        }
    }, [suggestion, pendingOrders, isAdjusting]);

    useEffect(() => {
        const handleOpenDistribution = () => { if (isMapVisible) onToggleMap(); };
        window.addEventListener('SAL_OPEN_DISTRIBUTION', handleOpenDistribution);
        return () => window.removeEventListener('SAL_OPEN_DISTRIBUTION', handleOpenDistribution);
    }, [isMapVisible, onToggleMap]);

    const hasAnalysis = !!suggestion && (suggestion.ownFleet.length > 0 || suggestion.thirdParty.length > 0 || !!suggestion.waitSuggestion || suggestion.heldOrders.length > 0);
    const { waitSuggestion = null, routes = [], sequentialOpportunity, smartWaitAnalysis, metrics } = suggestion || {};
    const logicText = smartWaitAnalysis?.message || waitSuggestion?.message || "Otimização Lógica Ativa: Roteamento baseado em SLA e Custo.";
    const reasonPart = logicText.includes("Motivo:") ? logicText.split("Motivo:")[1].trim() : logicText;

    const sortRoutesByTiming = (a: SuggestedRoute, b: SuggestedRoute) => {
        const priority: Record<string, number> = { 'critical': 0, 'optimal': 1, 'waiting_batch': 2, 'waiting_kitchen': 3 };
        const statusA = a.timing?.status || 'waiting_kitchen';
        const statusB = b.timing?.status || 'waiting_kitchen';
        return (priority[statusA] ?? 99) - (priority[statusB] ?? 99);
    };

    const ownRoutes = [...routes.filter(r => r.type === 'own')].sort(sortRoutesByTiming);
    const thirdPartyRoutes = [...routes.filter(r => r.type === 'third')].sort(sortRoutesByTiming);
    const signedDelta = metrics?.cashDelta.signedDelta || 0;
    const isExtraCost = signedDelta < 0;
    const decisionReason = metrics?.cashDelta.reason || 'UNKNOWN';
    const chosenCost = metrics?.cashDelta.chosen || 0;
    const alternativeCost = metrics?.cashDelta.alternative || 0;
    const ownCost = metrics?.breakdown.ownCash || 0;
    const thirdCost = metrics?.breakdown.thirdPartyCash || 0;
    const ownCount = suggestion?.ownFleet.length || 0;
    const tpCount = suggestion?.thirdParty.length || 0;
    const decisionTrace = (suggestion?.decisionTrace || []) as DecisionTraceEntry[];
    const oracleMultiplier = suggestion?.oracleMultiplier ?? 1;
    const isDispatcherProfile = userRole === 'dispatcher';
    const isAdminProfile = userRole === 'admin';
    const livePendingById = useMemo(
        () => new Map(pendingOrders.map((order) => [order.id, order])),
        [pendingOrders]
    );

    const formatMoney = (value?: number) => {
        if (typeof value !== 'number' || !Number.isFinite(value)) return 'n/d';
        return `R$ ${value.toFixed(2)}`;
    };

    const formatDecisionLabel = (label?: string) => {
        if (label === 'OWN_CLUSTER') return 'FROTA_PRÓPRIA';
        return label || 'DECISAO';
    };

    const getAuditReasonDescription = (reason: DecisionReason) => {
        switch (reason) {
            case 'CHEAPER': return "Arbitragem por maior margem com base no custo real entre as modalidades.";
            case 'CONSTRAINT_NO_FLEET': return "Frota própria indisponível ou sem capacidade no momento.";
            case 'CAPACITY_PRESSURE': return "Preservação estratégica da frota antes de pico previsto pelo Oráculo.";
            case 'SLA_PRIORITY': return "Priorização do prazo de entrega sobre o custo financeiro.";
            case 'SLA_E_ACTIVE': return "Brain Audit (SLA-e) Ativo: Validação rigorosa de viabilidade de rota via Google API.";
            case 'RETENTION_STRATEGY': return "Estratégia de Retenção ativa para economia futura.";
            default: return "Otimização baseada em histórico e parâmetros de configuração.";
        }
    };

    const mainAuditData = AuditDictionary.smartSavings(chosenCost, alternativeCost, getAuditReasonDescription(decisionReason));

    const handleConfirmAdjustment = (payload: { reason: string; ownRouteGroups: Record<string, string>; thirdRouteGroups: Record<string, string>; ownActiveRouteByOrder?: Record<string, string> }) => {
        const { reason, ownRouteGroups, thirdRouteGroups, ownActiveRouteByOrder } = payload;

        // v60.2: REMOVIDA validação obrigatória de driver
        // A validação v59.2 quebrou o fluxo normal de ajustes manuais
        // O driver será selecionado no momento do despacho, não no ajuste manual
        // Isso permite que o despachante faça ajustes manuais sem bloqueios

        const ownIds = localFleet.map(o => o.id);

        if (onBatchManualAssignment) {
            onBatchManualAssignment(ownIds, localApps.map(o => o.id), reason, ownRouteGroups, thirdRouteGroups, ownActiveRouteByOrder);
            toast.success("Distribuição manual salva!", { icon: '📍' });
        } else if (onManualAssignment) {
            localFleet.forEach(o => {
                onManualAssignment(o.id, 'own', reason);
            });
            localApps.forEach(o => {
                onManualAssignment(o.id, 'third', reason);
            });
            toast.success("Distribuição manual salva!", { icon: '📍' });
        }
        setIsAdjusting(false);
    };

    // State for Driver Selection Modal
    const [isSelectingDriver, setIsSelectingDriver] = useState(false);
    const [selectedRouteForDispatch, setSelectedRouteForDispatch] = useState<SuggestedRoute | null>(null);
    const activeDrivers = drivers?.filter(d => d.status === 'active') || [];

    const handleAttemptDispatch = (route: SuggestedRoute) => {
        const executeDispatch = (driverId?: string) => {
            onDispatchRoute(route.orders, route.type, driverId || route.assigneeId);
            setIsSelectingDriver(false);
            setSelectedRouteForDispatch(null);

            // Toast feedback based on action
            if (driverId) {
                const driverName = activeDrivers.find(d => d.id === driverId)?.name || 'Motoboy';
                toast.success(`Rota atribuída para ${driverName}!`, { icon: '🏍️' });
            } else if (route.assigneeId) {
                toast.success(`Despacho confirmado!`, { icon: '🚀' });
            } else {
                toast("Enviado para Fila de Espera", { icon: '⏳' });
            }
        };

        // Case 1: Route already has assignee (auto or manual)
        if (route.assigneeId) {
            // v57.9: Verificar se precisa aprovação de hora extra
            if (route.requiresOvertimeApproval) {
                toast((t) => (
                    <div className="flex flex-col gap-3 min-w-[280px]">
                        <div className="flex items-center gap-2 text-amber-500"><span className="font-black text-xs uppercase tracking-widest">Alerta de Hora Extra</span></div>
                        <div className="space-y-2">
                            <p className="text-sm text-text-primary font-bold leading-snug">{route.assigneeName} está fora do turno.</p>
                            <p className="text-xs text-text-muted leading-relaxed">Enviar rota mesmo assim? (Horas extras serão contabilizadas)</p>
                        </div>
                        <div className="flex gap-2 justify-end mt-2 pt-2 border-t border-border-dim">
                            <button type="button" onClick={() => toast.dismiss(t.id)} className="px-4 py-2 text-xs font-black text-text-muted hover:text-text-primary uppercase tracking-wider transition-colors">Cancelar</button>
                            <button type="button" onClick={() => { executeDispatch(); toast.dismiss(t.id); }} className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-badge-text-inv rounded-lg font-black text-xs uppercase tracking-widest shadow-xl">Confirmar Hora Extra</button>
                        </div>
                    </div>
                ), { duration: 8000, position: 'bottom-center', style: { background: '#0f172a', border: '1px solid rgba(245, 158, 11, 0.5)', padding: '16px', borderRadius: '1.25rem' } });
            } else if (route.timing?.status === 'waiting_batch') {
                toast((t) => (
                    <div className="flex flex-col gap-3 min-w-[280px]">
                        <div className="flex items-center gap-2 text-amber-500"><span className="font-black text-xs uppercase tracking-widest">Soberania do Operador</span></div>
                        <div className="space-y-2">
                            <p className="text-sm text-text-primary font-bold leading-snug">Sofia recomenda esperar.</p>
                            <p className="text-xs text-text-muted leading-relaxed">O prazo permite agrupar mais entregas. Despachar agora?</p>
                        </div>
                        <div className="flex gap-2 justify-end mt-2 pt-2 border-t border-border-dim">
                            <button type="button" onClick={() => toast.dismiss(t.id)} className="px-4 py-2 text-xs font-black text-text-muted hover:text-text-primary uppercase tracking-wider transition-colors">Esperar</button>
                            <button type="button" onClick={() => { executeDispatch(); toast.dismiss(t.id); }} className="px-4 py-2 bg-sal-burnt-orange hover:bg-sal-burnt-orange/80 text-badge-text-inv rounded-lg font-black text-xs uppercase tracking-widest shadow-xl">Despachar mesmo assim</button>
                        </div>
                    </div>
                ), { duration: 8000, position: 'bottom-center', style: { background: '#0f172a', border: '1px solid rgba(245, 158, 11, 0.5)', padding: '16px', borderRadius: '1.25rem' } });
            } else {
                executeDispatch();
            }
            return;
        }

        // Case 2: No assignee - Check Active Drivers
        if (route.type === 'own') {
            if (activeDrivers.length === 1) {
                // Auto-Assign Single Driver
                executeDispatch(activeDrivers[0].id);
            } else if (activeDrivers.length > 1) {
                // Open Selection Modal
                setSelectedRouteForDispatch(route);
                setIsSelectingDriver(true);
            } else {
                // v57.9: Bloquear despacho sem motorista - mostrar erro claro
                toast.error('Nenhum motorista ativo disponível. Ative um motoboy em Configurações > Frota.', {
                    duration: 5000,
                    icon: '⚠️'
                });
            }
        } else {
            // Third Party - Just Dispatch
            executeDispatch();
        }
    };

    const renderOrderCard = (o: Order, idx: number, type: 'own' | 'third', cumulativeDeliveryMinutes: number, analysis?: SafeDispatchAnalysis) => {
        const deliveryTime = Math.round(cumulativeDeliveryMinutes);
        const isOwn = type === 'own';
        
        // v58.4: Use provided analysis or fallback to local calculation
        const safeAnalysis = analysis || analyzeSafeDispatch(o, config, cumulativeDeliveryMinutes, isOwn);
        const slack = safeAnalysis.value;
        
        const timeLimit = safeAnalysis.deadline;
        const elapsed = safeAnalysis.elapsed;
        const tolerance = safeAnalysis.tolerance;
        const serviceTime = safeAnalysis.service;
        const kitchenRemaining = safeAnalysis.kitchen;

        const auditArrival = AuditDictionary.arrivalEstimate(deliveryTime, serviceTime, idx);
        const auditSlack = AuditDictionary.safeDispatch(timeLimit, elapsed, deliveryTime, tolerance, kitchenRemaining, serviceTime);
        
        const badgeInfo = slack < 0
            ? { label: 'ATRASADO', sub: `${Math.floor(slack)}m`, color: 'text-status-critical bg-status-critical/10 border-status-critical/30' }
            : slack < 10
                ? { label: 'LIMITE', sub: 'Risco Alto', color: 'text-status-warning bg-status-warning/10 border-status-warning/30' }
                : { label: 'NO PRAZO', sub: `Saída < ${Math.floor(slack)}m`, color: 'text-status-ready bg-status-ready/10 border-status-ready/30' };

        const borderColor = isOwn ? 'border-sal-solid-indigo' : 'border-status-warning';
        const textColor = isOwn ? 'text-sal-solid-indigo' : 'text-status-warning';

        const mainId = o.displayId ? `#${o.displayId}` : (o.shortCode ? `#${o.shortCode}` : `#${o.id.slice(-4)}`);

        const formatClientName = (name: string, isCompact: boolean) => {
            if (!name) return 'Cliente';
            const parts = name.trim().split(/\s+/);
            if (parts.length === 1) return parts[0];
            const limit = isCompact ? 2 : 3;
            const namesToShow = parts.slice(0, limit);
            if (parts.length > limit) {
                const lastIndex = namesToShow.length - 1;
                namesToShow[lastIndex] = namesToShow[lastIndex].charAt(0) + '.';
            }
            return namesToShow.join(' ');
        };

        return (
            <div key={o.id} className="flex items-center gap-2 relative z-10 group/item">
                <div className={`w-5 h-5 rounded-full bg-card-bg border-2 ${borderColor} text-xs font-bold ${textColor} flex items-center justify-center shadow-sm shrink-0`}>{idx + 1}</div>
                <div className="flex-1 bg-sal-gunmetal-light/5 rounded-lg p-1.5 border border-border-dim flex justify-between items-center group-hover/item:bg-sal-gunmetal-light/10 transition-colors">
                    <div className="min-w-0 flex-1 mr-2"><div className="flex items-center gap-2 mb-0.5"><span className={`text-xs font-mono font-bold opacity-70 ${textColor}`}>{mainId}</span><span className="text-sm font-bold text-text-primary truncate">{formatClientName(o.customerName, true)}</span></div><div className="text-xs text-text-muted uppercase truncate flex items-center gap-1">{o.neighborhood}</div></div>
                    <div className="flex flex-col items-end gap-1"><div className="flex items-center gap-2 text-xs font-mono text-text-muted"><span>{o.distance?.toFixed(1)} km</span><BaseTooltip data={auditArrival}><div className="flex items-center gap-1 cursor-help"><span className="font-bold text-text-secondary">Chegada: {deliveryTime}m</span></div></BaseTooltip></div>
                        <div className="flex items-center gap-2">{config.driversCollectPayments && (<span className={`text-xs font-mono font-bold ${textColor}`}>{o.orderValue ? `R$ ${o.orderValue.toFixed(0)}` : '-'}</span>)}<BaseTooltip data={auditSlack}><div className={`px-1.5 py-0.5 rounded border text-xs font-bold uppercase flex flex-col items-end leading-none ${badgeInfo.color}`}><span>{badgeInfo.label}</span>{badgeInfo.sub && <span className="text-xs opacity-80 mt-0.5 font-normal ">{badgeInfo.sub}</span>}</div></BaseTooltip></div></div>
                </div>
            </div>
        );
    };

    const ReadinessBadge = ({ timing }: { timing?: TimingGuidance }) => {
        if (!timing) return null;
        const colors = {
            waiting_kitchen: 'bg-status-pending/10 text-status-pending border-status-pending/30',
            waiting_batch: 'bg-status-info/10 text-status-info border-status-info/30',
            optimal: 'bg-status-ready/10 text-status-ready border-status-ready/30',
            critical: 'bg-status-critical/10 text-status-critical border-status-critical/30 animate-pulse'
        };
        return (<BaseTooltip data={AuditDictionary.readiness(timing.reason)}><div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-black uppercase tracking-wider ${colors[timing.status]}`}><div className={`w-1.5 h-1.5 rounded-full ${timing.status === 'optimal' || timing.status === 'critical' ? 'bg-current animate-pulse' : 'bg-current'}`} />{timing.label}</div></BaseTooltip>);
    };

    const confirmStrategy = (strategyType?: string, orderIds?: string[]) => {
        if (!strategyType || !orderIds?.length || !handleConfirmStrategy) return;
        handleConfirmStrategy(orderIds, strategyType);
    };

    const ignoreStrategy = (strategyType?: string, orderIds?: string[], reason?: string) => {
        if (!strategyType || !orderIds?.length || !handleIgnoreStrategy) return;
        // v58.6: A lógica de atualização otimista agora está em useSalActions
        // handleIgnoreStrategy já atualiza o estado otimista internamente
        handleIgnoreStrategy(orderIds, strategyType, reason);
    };

    const hasResolvedStrategy = (order: Order, strategy: string) => {
        const liveOrder = livePendingById.get(order.id) || order;
        const ignored = liveOrder.ignoredStrategies || [];
        const accepted = liveOrder.acceptedStrategies || [];
        // v58.6: Verificar estado persistido e estado otimista do useSalActions
        const optimisticallyIgnored = isStrategyIgnored?.(order.id, strategy) ?? false;
        return ignored.includes(strategy) || accepted.includes(strategy) || optimisticallyIgnored;
    };

    const retentionOrders = [
        ...(smartWaitAnalysis?.readyOrders || []),
        ...(smartWaitAnalysis?.kitchenOrders || [])
    ];
    const retentionStrategyType = smartWaitAnalysis?.readyOrders?.length > 0 && smartWaitAnalysis?.kitchenOrders?.length > 0
        ? 'dispatch_hold'
        : 'production_retention';
    const isRetentionResolved = retentionOrders.length > 0 && retentionOrders.every(o => hasResolvedStrategy(o, retentionStrategyType));

    const boomerangOrders = sequentialOpportunity?.trip1 || [];
    const boomerangReturnOrders = sequentialOpportunity?.trip2 || [];
    const allBoomerangOrders = [...boomerangOrders, ...boomerangReturnOrders];
    const isBoomerangResolved = allBoomerangOrders.length > 0 && allBoomerangOrders.every(o => hasResolvedStrategy(o, 'boomerang'));

    const openJustificationModal = (
        strategyType: 'production_retention' | 'dispatch_hold' | 'boomerang',
        strategyOrders: Order[],
        modalDescription: string,
        strategySavings: number,
        strategyDelayImpact: number,
        fallbackReason: string
    ) => {
        if (openSofiaDecision) {
            openSofiaDecision({
                mode: 'justification',
                strategyType,
                orders: strategyOrders,
                savings: strategySavings,
                delayImpact: strategyDelayImpact,
                description: modalDescription,
                onConfirm: () => {}
            });
            return;
        }

        ignoreStrategy(strategyType, strategyOrders.map((o) => o.id), fallbackReason);
    };

    const handleAcceptRetentionStrategy = () => {
        if (!retentionOrders.length) return;
        onAcceptWait?.(retentionOrders);
        confirmStrategy(retentionStrategyType, retentionOrders.map((o) => o.id));
    };

    const handleRejectRetentionStrategy = (descriptionText: string) => {
        if (!retentionOrders.length || !smartWaitAnalysis) return;
        openJustificationModal(
            retentionStrategyType,
            retentionOrders,
            descriptionText,
            smartWaitAnalysis.projectedSavings,
            smartWaitAnalysis.delayImpact || 8,
            'Retention strategy rejected'
        );
    };

    const handleAcceptBoomerangStrategy = () => {
        const trip1Ids = boomerangOrders.map((o) => o.id);
        const trip2Ids = boomerangReturnOrders.map((o) => o.id);

        onApplySequential?.();
        if (handleConfirmBoomerangStrategy) {
            handleConfirmBoomerangStrategy(trip1Ids, trip2Ids);
            return;
        }

        confirmStrategy('boomerang', [...trip1Ids, ...trip2Ids]);
    };

    const handleRejectBoomerangStrategy = (descriptionText: string) => {
        if (!allBoomerangOrders.length) return;
        openJustificationModal(
            'boomerang',
            allBoomerangOrders,
            descriptionText,
            0,
            0,
            'Boomerang strategy rejected'
        );
    };

    // Nova Visualização de Estratégia (Card ao invés de Ribbon)
    const StrategyStatusCard = () => {
        // Não mostrar nada quando não há análise ou estratégia ativa
        if (!hasAnalysis) return null;

        const isDrainage = reasonPart.toLowerCase().includes('drenagem');
        const isBoomerang = suggestion?.sequentialOpportunity?.active;
        const isRetention = smartWaitAnalysis?.active;
        const isPreservationActive = (suggestion?.metrics?.preservationMarkup || 0) > 0;
        const isCurrentStrategyResolved = (isRetention && isRetentionResolved) || (isBoomerang && isBoomerangResolved);

        if (isCurrentStrategyResolved) {
            return null;
        }

        const affectedOrders = isRetention
            ? [...(smartWaitAnalysis?.readyOrders || []), ...(smartWaitAnalysis?.kitchenOrders || [])]
            : isBoomerang
                ? (suggestion?.sequentialOpportunity?.trip1 || [])
                : [];

        const orderIdsText = affectedOrders.length > 0
            ? ` (${affectedOrders.length > 1 ? 'Pedidos' : 'Pedido'} ${affectedOrders.map(o => {
                const idLabel = o.displayId || o.shortCode || o.id.slice(-4);
                const firstName = o.customerName?.split(' ')[0] || 'Cliente';
                return `#${idLabel} (${firstName})`;
            }).join(', ')})`
            : '';

        const primaryTrace = decisionTrace[0];
        const headlineReason = isDispatcherProfile
            ? (primaryTrace?.rationale || reasonPart)
            : `${reasonPart}${orderIdsText}`;

        let accentColor = 'text-sal-cyber-teal';
        let borderColor = 'border-sal-cyber-teal/30';
        let bgGradient = 'from-sal-cyber-teal/5 to-transparent';
        let icon = <Cpu className="w-4 h-4 text-sal-cyber-teal" />;
        let title = "Otimização Padrão";

        if (isDrainage) {
            accentColor = 'text-status-critical';
            borderColor = 'border-status-critical animate-pulse';
            bgGradient = 'from-status-critical/20 via-status-critical/5 to-transparent';
            icon = <Zap className="w-4 h-4 text-status-critical" />;
            title = "DRENAGEM OPERACIONAL";
        } else if (isBoomerang) {
            accentColor = 'text-sal-solid-indigo';
            borderColor = 'border-sal-solid-indigo';
            bgGradient = 'from-sal-solid-indigo/20 via-sal-solid-indigo/5 to-transparent';
            icon = <Repeat2 className="w-4 h-4 text-sal-solid-indigo" />;
            title = "ESTRATÉGIA BUMERANGUE";
        } else if (isRetention) {
            accentColor = 'text-status-info';
            borderColor = 'border-status-info';
            bgGradient = 'from-status-info/20 via-status-info/5 to-transparent';
            icon = <Snowflake className="w-4 h-4 text-status-info" />;
            title = "RETENÇÃO DE PRODUÇÃO";
        } else if (isPreservationActive) {
            accentColor = 'text-emerald-400';
            borderColor = 'border-emerald-400';
            bgGradient = 'from-emerald-400/20 via-emerald-400/5 to-transparent';
            icon = <ShieldCheck className="w-4 h-4 text-emerald-400" />;
            title = "PRESERVAÇÃO DE FROTA";
        }

        return (
            <div
                className={`relative overflow-hidden rounded-lg border ${borderColor} ${isGlassMode ? '' : 'bg-card-bg'} mt-2 transition-all duration-300`}
                style={isGlassMode ? { backgroundColor: 'rgba(var(--glass-card-bg))', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' } : {}}
            >
                <div className={`absolute inset-0 bg-gradient-to-r ${bgGradient} opacity-60`} />
                <div className="relative z-10 p-2">
                    <div className={`flex ${isMapVisible ? 'flex-col gap-1' : 'justify-between items-center gap-3'}`}>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                                <span className="w-4 h-4 flex items-center justify-center">{icon}</span>
                                <h3 className={`text-sm font-black uppercase tracking-wider ${accentColor}`}>{title}</h3>
                                {config.oracle?.active && (
                                    <span className="text-[10px] font-bold text-status-info ml-1">x{oracleMultiplier.toFixed(2)}</span>
                                )}
                            </div>
                            <p className="text-xs text-text-secondary font-medium leading-snug mt-0.5 truncate">
                                {headlineReason}
                            </p>
                        </div>
                        {/* Ações da Estratégia */}
                        <div className={`flex items-center gap-1.5 shrink-0 ${isMapVisible ? 'justify-center border-t border-border-dim pt-2 mt-1' : ''}`}>
                            {isRetention ? (
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleAcceptRetentionStrategy();
                                        }}
                                        className={`px-3 py-1.5 text-white border rounded-lg text-sm font-bold uppercase transition-all flex items-center gap-1.5 ${config.isSolarMode ? 'bg-status-info hover:brightness-90 border-status-info/20' : 'bg-status-info hover:bg-status-info/80 border-status-info/50 animate-pulse'}`}
                                    >
                                        <Snowflake className="w-3.5 h-3.5" /> Aguardar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleRejectRetentionStrategy(reasonPart + orderIdsText);
                                        }}
                                        className={`px-2.5 py-1.5 rounded-lg transition-colors text-xs font-bold uppercase flex items-center gap-1.5 border ${config.isSolarMode ? 'text-slate-500 hover:text-slate-800 hover:bg-slate-100 border-slate-200' : 'text-text-muted hover:text-white hover:bg-white/5 border-border-dim hover:border-text-muted'}`}
                                    >
                                        <X className="w-3.5 h-3.5" /> {smartWaitAnalysis?.kitchenOrders?.length > 0 ? 'Ignorar e Forçar Preparo' : 'Ignorar e Despachar Já'}
                                    </button>
                                </div>
                            ) : (suggestion?.isSplitRecommended || isBoomerang) && (
                                <div className="flex items-center gap-2">
                                    {isBoomerang && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleAcceptBoomerangStrategy();
                                                }}
                                                className={`px-3 py-1.5 font-bold rounded-lg text-sm uppercase transition-all flex items-center gap-1.5 bg-sal-cyber-teal ${config.isSolarMode ? 'hover:brightness-90' : 'hover:brightness-110'} text-sal-gunmetal shadow-md`}
                                            >
                                                <Zap className="w-3.5 h-3.5" /> Aceitar Bumerangue
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleRejectBoomerangStrategy(`Preparar bumerangue para ${suggestion?.sequentialOpportunity?.driverName}${orderIdsText}`);
                                                }}
                                                className={`px-2.5 py-1.5 rounded-lg transition-colors text-xs font-bold uppercase flex items-center gap-1.5 border ${config.isSolarMode ? 'text-slate-500 hover:text-slate-800 hover:bg-slate-100 border-slate-200' : 'text-text-muted hover:text-white hover:bg-white/5 border-border-dim hover:border-text-muted'}`}
                                            >
                                                <X className="w-3.5 h-3.5" /> Recusar
                                            </button>
                                        </>
                                    )}
                                    {decisionTrace.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setShowDecisionTrace(prev => !prev)}
                                            className={`px-2.5 py-1.5 rounded-lg transition-colors text-xs font-bold uppercase flex items-center gap-1.5 border ${config.isSolarMode ? 'text-slate-500 hover:text-slate-800 hover:bg-slate-100 border-slate-200' : 'text-text-muted hover:text-white hover:bg-white/5 border-border-dim hover:border-text-muted'}`}
                                        >
                                            {showDecisionTrace ? 'Ocultar' : 'Por que?'}
                                        </button>
                                    )}
                                    {isDrainage && (
                                        <div className="px-3 py-1.5 bg-status-critical text-white font-black text-xs uppercase tracking-wider rounded-lg flex items-center gap-2 shadow-lg animate-pulse">
                                            <AlertCircle className="w-4 h-4" />
                                            <span>Modo Drenagem</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {showDecisionTrace && decisionTrace.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-border-dim space-y-2">
                            {(isAdminProfile ? decisionTrace.slice(0, 5) : decisionTrace.slice(0, 1)).map((trace, idx) => {
                                const comparedOwnCost = trace.ownCost;
                                const comparedThirdCost = trace.thirdCost;

                                return (
                                    <div key={`${trace.chosenLabel}_${idx}`} className="text-xs text-text-muted leading-relaxed">
                                        <span className="font-black text-text-primary mr-1">{formatDecisionLabel(trace.chosenLabel)}:</span>
                                        <span>{trace.rationale}</span>
                                        <span className="ml-2 font-mono opacity-80">
                                            {`FROTA ${formatMoney(comparedOwnCost)} vs APP ${formatMoney(comparedThirdCost)}`}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                </div>
            </div>
        );
    };

    return (
        <div className={`h-full w-full flex flex-col relative overflow-hidden animate-in fade-in duration-300`}
            style={isGlassMode ? { backgroundColor: 'transparent' } : { backgroundColor: 'rgb(var(--bg-main))' }}>
            {/* v59.1: Modal de estratégias removido - agora gerenciado pelo App.tsx via fila de sugestões */}

            {/* Header Compacto - Tudo em uma linha */}
            <div
                className={`shrink-0 py-2 px-3 border-b border-border-dim z-20`}
                style={isGlassMode ? { backgroundColor: 'rgba(var(--glass-panel-bg))', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' } : { backgroundColor: 'rgb(var(--card-bg))', boxShadow: 'var(--shadow-sm)' }}
            >
                <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        <div className="shrink-0 rounded border border-sal-cyber-teal/20 bg-sal-cyber-teal/10 p-1"><Cpu className="w-4 h-4 text-sal-cyber-teal" /></div>
                        <div className="flex min-w-0 items-center gap-2 lg:gap-3">
                            <h2 className="truncate text-sm font-black uppercase leading-none tracking-tight text-text-primary lg:text-base">Cérebro Logístico</h2>
                            {activeDrivers.length > 0 && !isMapVisible && (
                                <div className="hidden shrink-0 items-center gap-1 text-xs font-bold uppercase tracking-wide text-sal-solid-indigo opacity-80 xl:flex">
                                    <Users className="h-3 w-3" />
                                    <span>{activeDrivers.length} na Rede</span>
                                </div>
                            )}
                            <div className={`shrink-0 text-xs font-bold uppercase tracking-wide text-text-muted ${isMapVisible ? 'hidden' : 'hidden lg:block'}`}>
                                Recalculo em {Math.max(0, secondsToNextUpdate)}s
                            </div>
                            {isMapVisible && (
                                <div className="shrink-0 rounded border border-border-dim px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-text-muted">
                                    {Math.max(0, secondsToNextUpdate)}s
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-1 pl-1">
                        {onToggleGlassMode && (
                            <button
                                type="button"
                                onClick={onToggleGlassMode}
                                className={`p-1.5 rounded transition-all border ${isGlassMode ? 'bg-sal-cyber-teal/20 border-sal-cyber-teal/50 text-sal-cyber-teal shadow-[0_0_10px_rgba(6,182,212,0.2)]' : 'border-transparent text-icon-muted hover:bg-black/5 dark:hover:bg-white/5'}`}
                                title={isGlassMode ? "Desativar Estilo Glass" : "Ativar Estilo Glass"}
                            >
                                <Sparkles className={`w-3.5 h-3.5 ${isGlassMode ? 'animate-pulse' : ''}`} />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onToggleMap}
                            className="shrink-0 rounded border border-transparent p-1.5 text-text-muted transition-colors hover:border-border-strong hover:bg-black/5 hover:text-text-primary dark:hover:bg-white/5"
                            title={isMapVisible ? "Expandir Listas" : "Ver Mapa"}
                            aria-label={isMapVisible ? "Expandir listas" : "Ver mapa"}
                        >
                            {isMapVisible ? <Minimize2 className="w-3.5 h-3.5" /> : <MapIcon className="w-3.5 h-3.5" />}
                        </button>
                    </div>
                </div>

                <StrategyStatusCard />
            </div>

            {/* Layout Condicional: Coluna única no Sidebar (Mapa) vs Coluna Dupla na Tela Cheia (Sem Mapa) */}
            <div className={`flex-1 flex overflow-hidden relative ${isMapVisible ? 'flex-col' : 'flex-row'}`}
                style={isGlassMode ? { backgroundColor: 'transparent' } : { backgroundColor: 'rgb(var(--bg-main))' }}>
                {/* Rotas Internas */}
                <div className={`flex-1 p-2 overflow-y-auto z-10 scrollbar-hide ${isMapVisible ? 'border-b' : 'border-r'} border-border-dim`}>
                    <div
                        className={`flex items-center justify-between mb-2 sticky top-0 z-20 py-2 border-b border-border-dim`}
                        style={{ backgroundColor: isGlassMode ? 'rgba(var(--glass-panel-bg))' : 'rgba(var(--bg-main), 0.9)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                    >
                        <h3 className="text-xs font-black text-sal-solid-indigo uppercase tracking-[0.2em] flex items-center gap-2"><Route className="w-3.5 h-3.5" /> Rotas Internas ({ownRoutes.length})</h3>
                    </div>
                    <div className="space-y-2">
                        {ownRoutes.length > 0 ? ownRoutes.map((route, i) => {
                            const routeTag = formatRouteTag(route.orders[0]?.smartClusterId, i);
                            
                            // v58.1: Unified safe-dispatch sequence calculation
                            const stops = calculateSafeDispatchSequence(route.orders, config, true, [], undefined);
                            const routeSafeDispatchLimit = Math.min(...stops.map(s => s.analysis.value));
                            const routeOrdersWithTimes = stops.map(s => ({ 
                                order: s.order, 
                                deliveryTime: s.cumulativeRouteMinutes - s.analysis.service 
                            }));

                            // v56.5: Calculate return leg to hub for ETR display
                            const lastOrder = route.orders[route.orders.length - 1];
                            const returnDist = (lastOrder?.lat && lastOrder?.lng) ? DistanceService.getDistanceSync(lastOrder.lat, lastOrder.lng, config.businessLat, config.businessLng) : 0;
                            const returnMins = route.estimatedReturnMinutes || Math.max(2, returnDist * COLD_START_MINS_PER_KM);
                            const totalRoundTripMins = Math.ceil(stops[stops.length - 1].cumulativeRouteMinutes + returnMins);
                            return (
                                <div
                                    key={`${routeTag}_${route.orders[0]?.id || i}`}
                                    className={`border border-border-dim rounded-xl group overflow-hidden hover:border-sal-solid-indigo/30 transition-colors`}
                                    style={isGlassMode ? { backgroundColor: 'rgba(var(--glass-card-bg))', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' } : { backgroundColor: 'rgb(var(--card-bg))', boxShadow: 'var(--shadow-md)' }}
                                >
                                    <div className="bg-sal-solid-indigo/5 px-2 py-1.5 border-b border-border-dim grid grid-cols-12 items-center gap-2">
                                        <div className="col-span-8 flex items-center gap-1.5 xl:gap-2 min-w-0">
                                            <div className="w-5 h-5 xl:w-6 xl:h-6 rounded-lg bg-sal-solid-indigo flex items-center justify-center text-badge-text-inv font-black text-xs shadow-sm shrink-0">{route.assigneeName.charAt(0)}</div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex justify-between items-center pr-2">
                                                    <div className="font-bold text-text-primary text-xs xl:text-xs truncate uppercase">{route.assigneeName}</div>
                                                    <div className={`font-black text-xs ${route.cost === 0 || route.cost > 9000 ? 'text-status-critical animate-pulse' : 'text-sal-solid-indigo'}`}>
                                                        {route.cost === 0 ? '⚠️ R$ 0,00' : route.cost > 9000 ? '⚠️ ERRO' : `R$ ${route.cost.toFixed(2)}`}
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    <span className="text-xs font-black px-1.5 py-0.5 rounded bg-sal-solid-indigo/10 text-sal-solid-indigo">{routeTag}</span>
                                                    <span className="text-xs font-black px-1.5 py-0.5 rounded bg-sal-solid-indigo/10 text-sal-solid-indigo">{getFleetLabel(route.type)}</span>
                                                </div>
                                                <div className="flex flex-wrap gap-1 mt-0.5 items-center">
                                                    <ReadinessBadge timing={route.timing} />
                                                    <span className={`text-xs font-bold px-1 rounded ${routeSafeDispatchLimit < 5 ? 'bg-status-critical/10 text-status-critical' : 'bg-status-info/10 text-status-info'}`}>
                                                        Sair em {routeSafeDispatchLimit}m
                                                    </span>
                                                    <span className="text-xs font-bold px-1 rounded bg-sal-gunmetal-light/10 text-text-muted" title={`Tempo total da rota: ${totalRoundTripMins} minutos`}>
                                                        Volta {formatETA(totalRoundTripMins)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-span-4 flex items-center justify-end gap-2">
                                            <button type="button" onClick={() => handleAttemptDispatch(route)} className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 font-bold text-xs uppercase tracking-wide active:scale-95 ${route.timing?.status === 'optimal' || route.timing?.status === 'critical' ? 'bg-sal-solid-indigo hover:brightness-110 text-badge-text-inv shadow-md' : 'bg-sal-gunmetal-light/10 text-text-muted hover:bg-sal-gunmetal-light/20 border border-border-dim'}`}>
                                                <PlayCircle className="w-3.5 h-3.5" />
                                                <span>Enviar</span>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="p-1.5 space-y-1.5">{routeOrdersWithTimes.map((item, idx) => renderOrderCard(item.order, idx, 'own', item.deliveryTime, stops[idx].analysis))}</div>
                                </div>
                            )
                        }) : (<div className="border border-dashed border-border-dim rounded-xl p-8 flex flex-col items-center justify-center text-text-muted"><Bike className="w-8 h-8 mb-2" /><span className="text-xs font-black uppercase tracking-widest text-center">Sem rotas</span></div>)}
                    </div>
                </div>

                <div className="flex-1 p-2 overflow-y-auto z-10 scrollbar-hide">
                    <div
                        className={`flex items-center justify-between mb-2 sticky top-0 z-20 py-2 border-b border-border-dim`}
                        style={{ backgroundColor: isGlassMode ? 'rgba(var(--glass-panel-bg))' : 'rgba(var(--bg-main), 0.9)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                    >
                        <h3 className="text-xs font-black text-sal-burnt-orange uppercase tracking-[0.2em] flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> Envios Externos ({thirdPartyRoutes.length})</h3>
                    </div>
                    <div className="space-y-2">
                        {thirdPartyRoutes.length > 0 ? thirdPartyRoutes.map((route, i) => {
                            const routeTag = formatRouteTag(route.orders[0]?.smartClusterId, i);
                            
                            // v58.1: Unified safe-dispatch sequence calculation
                            const stops = calculateSafeDispatchSequence(route.orders, config, false, [], undefined);
                            const routeSafeDispatchLimit = Math.min(...stops.map(s => s.analysis.value));
                            const routeOrdersWithTimes = stops.map(s => ({ 
                                order: s.order, 
                                deliveryTime: s.cumulativeRouteMinutes - s.analysis.service 
                            }));

                            return (
                                <div
                                    key={`${routeTag}_${route.orders[0]?.id || i}`}
                                    className={`border border-border-dim rounded-xl group overflow-hidden hover:border-sal-burnt-orange/30 transition-colors`}
                                    style={isGlassMode ? { backgroundColor: 'rgba(var(--glass-card-bg))', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' } : { backgroundColor: 'rgb(var(--card-bg))', boxShadow: 'var(--shadow-md)' }}
                                >
                                    <div className="bg-sal-burnt-orange/5 px-2 py-1.5 border-b border-border-dim grid grid-cols-12 items-center gap-2">
                                        <div className="col-span-8 flex items-center gap-1.5 xl:gap-2 min-w-0">
                                            <div className="w-5 h-5 xl:w-6 xl:h-6 rounded-lg bg-sal-burnt-orange flex items-center justify-center text-badge-text-inv font-black shadow-sm shrink-0"><Truck className="w-3 h-3" /></div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex justify-between items-center pr-2">
                                                    <div className="font-bold text-text-primary text-xs truncate uppercase">{route.assigneeName.split(' #')[0]}</div>
                                                    <div className={`font-black text-xs ${route.cost === 0 || route.cost > 9000 ? 'text-status-critical animate-pulse' : 'text-sal-burnt-orange'}`}>
                                                        {route.cost === 0 ? '⚠️ R$ 0,00' : route.cost > 9000 ? '⚠️ ERRO' : `R$ ${route.cost.toFixed(2)}`}
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    <span className="text-xs font-black px-1.5 py-0.5 rounded bg-sal-burnt-orange/10 text-sal-burnt-orange">{routeTag}</span>
                                                    <span className="text-xs font-black px-1.5 py-0.5 rounded bg-sal-burnt-orange/10 text-sal-burnt-orange">{getFleetLabel(route.type)}</span>
                                                    <span className="text-xs font-black px-1.5 py-0.5 rounded bg-sal-burnt-orange/10 text-sal-burnt-orange">{route.assigneeName.split(' #')[0]}</span>
                                                </div>
                                                <div className="flex flex-wrap gap-1 mt-0.5 items-center">
                                                    <ReadinessBadge timing={route.timing} />
                                                    <span className={`text-xs font-bold px-1 rounded ${routeSafeDispatchLimit < 5 ? 'bg-status-critical/10 text-status-critical' : 'bg-status-info/10 text-status-info'}`}>
                                                        Sair em {routeSafeDispatchLimit}m
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-span-4 flex items-center justify-end gap-2">
                                            <button type="button" onClick={() => handleAttemptDispatch(route)} className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 font-bold text-xs uppercase tracking-wide active:scale-95 ${route.timing?.status === 'optimal' || route.timing?.status === 'critical' ? `bg-sal-burnt-orange ${config.isSolarMode ? 'hover:brightness-90' : 'hover:brightness-110'} text-badge-text-inv shadow-md` : 'bg-sal-gunmetal-light/10 text-text-muted hover:bg-sal-gunmetal-light/20 border border-border-dim'}`}>
                                                <Send className="w-3.5 h-3.5" />
                                                <span>Enviar</span>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="p-1.5 space-y-1.5">{routeOrdersWithTimes.map((item, idx) => renderOrderCard(item.order, idx, 'third', item.deliveryTime, stops[idx].analysis))}</div>
                                </div>
                            )
                        }) : (<div className="border border-dashed border-border-dim rounded-xl p-8 flex flex-col items-center justify-center text-text-muted"><Truck className="w-8 h-8 mb-2" /><span className="text-xs font-black uppercase tracking-widest text-center">Sem envios</span></div>)}
                    </div>
                </div>
            </div>

            <div
                className={`p-4 border-t border-border-dim flex justify-between items-center shrink-0 z-20`}
                style={isGlassMode ? { backgroundColor: 'rgba(var(--glass-panel-bg))', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' } : { backgroundColor: 'rgb(var(--card-bg))', boxShadow: 'var(--shadow-lg)' }}
            >
                <button type="button" onClick={() => { if (isMapVisible) onToggleMap(); setIsAdjusting(true); }} className="flex items-center gap-2 px-3 py-2 bg-transparent hover:bg-black/5 dark:hover:bg-white/5 text-text-muted hover:text-text-primary rounded-lg font-bold text-xs transition-all uppercase tracking-wide border border-border-dim hover:border-border-strong">
                    <ArrowRightLeft className="w-3.5 h-3.5" /> Ajuste Manual
                </button>

            </div>

            {/* v46.8: Driver Selection Modal */}
            <AnimatePresence>
                {isSelectingDriver && selectedRouteForDispatch && activeDrivers.length > 0 && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-sal-gunmetal w-full max-w-sm rounded-2xl shadow-2xl border border-border-dim overflow-hidden">
                            <div className="p-4 border-b border-border-dim bg-black/5 dark:bg-white/5 flex items-center justify-between">
                                <h3 className="text-base font-black text-text-primary uppercase tracking-wider flex items-center gap-2">
                                    <Truck className="w-4 h-4 text-sal-cyber-teal" /> Selecionar Motoboy
                                </h3>
                                <button type="button" onClick={() => setIsSelectingDriver(false)} className="text-text-muted hover:text-text-primary transition-colors"><X className="w-5 h-5" /></button>
                            </div>
                            <div className="p-4 max-h-[60vh] overflow-y-auto space-y-2 no-scrollbar">
                                {activeDrivers.map(driver => (
                                    <button type="button" key={driver.id} onClick={() => handleAttemptDispatch({ ...selectedRouteForDispatch, assigneeId: driver.id })} className="w-full flex items-center justify-between p-3 rounded-xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-border-dim hover:border-sal-cyber-teal/30 transition-all group">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-sal-solid-indigo flex items-center justify-center font-black text-badge-text-inv text-base shadow-lg group-hover:scale-110 transition-transform">{driver.name.charAt(0)}</div>
                                            <div className="text-left">
                                                <div className="font-bold text-text-primary text-base">{driver.name}</div>
                                                <div className="text-xs text-text-muted font-medium">{driver.status === 'active' ? 'Disponível' : 'Ocupado'}</div>
                                            </div>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-icon-muted group-hover:text-sal-cyber-teal" />
                                    </button>
                                ))}
                                <button type="button" onClick={() => handleAttemptDispatch(selectedRouteForDispatch)} className="w-full flex items-center justify-center p-3 mt-4 rounded-xl border border-dashed border-border-dim hover:border-text-secondary hover:text-text-primary text-text-muted text-sm font-bold uppercase tracking-wide transition-all">
                                    Despachar sem Motoboy (Fila de Espera)
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <ManualIntervention isAdjusting={isAdjusting} setIsAdjusting={setIsAdjusting} localFleet={localFleet} localApps={localApps} localTriage={localTriage} setLocalFleet={setLocalFleet} setLocalApps={setLocalApps} setLocalTriage={setLocalTriage} onConfirmAdjustment={handleConfirmAdjustment} isGlassMode={isGlassMode} activeDriverRoutes={activeDriverRoutes} />
        </div>
    );
};
